import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI, businessAdsAPI } from '../../lib/api';
import { Megaphone, Plus, Loader2, X, Pencil, Trash2, ToggleLeft, ToggleRight, Eye, MousePointerClick, Image as ImageIcon, Upload, ChevronLeft, ChevronRight, ExternalLink, AlertTriangle, Mail, TrendingUp, BarChart3, Activity } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import api from '../../lib/api';

const AD_CATEGORIES = [
  "Restaurant", "Retail", "Automotive", "Health & Wellness", "Real Estate",
  "Professional Services", "Home Services", "Education", "Entertainment",
  "Technology", "Financial Services", "Beauty & Spa", "Fitness", "Other"
];

const CTA_OPTIONS = ["Learn More", "Visit Website", "Call Now", "Get Directions", "Book Now", "Shop Now", "Contact Us"];

function formatDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getAdStatus(ad) {
  const now = new Date().toISOString();
  if (!ad.is_active) return { label: 'Paused', color: 'bg-amber-50 text-amber-700' };
  if (ad.start_date && ad.start_date > now) return { label: 'Scheduled', color: 'bg-sky-50 text-sky-700' };
  if (ad.end_date && ad.end_date !== '' && ad.end_date < now) return { label: 'Expired', color: 'bg-zinc-100 text-zinc-500' };
  return { label: 'Active', color: 'bg-emerald-50 text-emerald-700' };
}

function ctr(impressions, clicks) {
  if (!impressions) return '0.0%';
  return ((clicks / impressions) * 100).toFixed(1) + '%';
}

function ImageUploader({ label, value, onChange, testId }) {
  const [uploading, setUploading] = useState(false);
  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(data.url);
      toast.success('Image uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">{label}</label>
      {value ? (
        <div className="relative group w-16 h-16 rounded-lg overflow-hidden border border-zinc-200">
          <img src={`${process.env.REACT_APP_BACKEND_URL}${value}`} alt="" className="w-full h-full object-cover" />
          <button onClick={() => onChange('')} className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
            <X className="w-4 h-4 text-white" />
          </button>
        </div>
      ) : (
        <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-zinc-300 rounded-lg text-sm text-zinc-500 cursor-pointer hover:border-[#002FA7] hover:text-[#002FA7] transition-colors" data-testid={testId}>
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {uploading ? 'Uploading...' : 'Upload'}
          <input type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
        </label>
      )}
    </div>
  );
}

function GalleryUploader({ images, onChange }) {
  const [uploading, setUploading] = useState(false);
  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const uploaded = [...images];
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const { data } = await api.post('/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        uploaded.push(data.url);
      } catch { /* skip failed */ }
    }
    onChange(uploaded);
    setUploading(false);
    toast.success('Images uploaded');
  };
  const removeImage = (idx) => onChange(images.filter((_, i) => i !== idx));
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1.5 block">Image Gallery</label>
      <div className="flex flex-wrap gap-2 mb-2">
        {images.map((img, i) => (
          <div key={i} className="relative group w-16 h-16 rounded-lg overflow-hidden border border-zinc-200">
            <img src={`${process.env.REACT_APP_BACKEND_URL}${img}`} alt="" className="w-full h-full object-cover" />
            <button onClick={() => removeImage(i)} className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
        ))}
        <label className="flex flex-col items-center justify-center w-16 h-16 border border-dashed border-zinc-300 rounded-lg text-zinc-400 cursor-pointer hover:border-[#002FA7] hover:text-[#002FA7] transition-colors" data-testid="gallery-upload-btn">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4" /><span className="text-[9px]">Add</span></>}
          <input type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} disabled={uploading} />
        </label>
      </div>
    </div>
  );
}

function BusinessAdForm({ ad, onSave, onClose }) {
  const isEdit = !!ad;
  const [form, setForm] = useState({
    business_name: '', category: '', address: '', city: '', province: 'Ontario',
    postal_code: '', phone: '', website: '', email: '', ad_title: '', ad_description: '',
    logo_image: '', image_gallery: [], cta_text: 'Learn More', cta_url: '',
    priority: 1, start_date: new Date().toISOString().slice(0, 10), end_date: '', is_active: true,
    ...(ad || {})
  });
  const [submitting, setSubmitting] = useState(false);

  // Format dates for inputs
  useEffect(() => {
    if (ad) {
      setForm(prev => ({
        ...prev,
        start_date: ad.start_date ? ad.start_date.slice(0, 10) : '',
        end_date: ad.end_date ? ad.end_date.slice(0, 10) : '',
      }));
    }
  }, [ad]);

  const set = (key) => (e) => setForm(prev => ({ ...prev, [key]: e.target?.value !== undefined ? e.target.value : e }));

  const handleSubmit = async () => {
    if (!form.business_name.trim()) { toast.error('Business name required'); return; }
    if (!form.ad_title.trim()) { toast.error('Ad title required'); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        priority: parseInt(form.priority) || 1,
        start_date: form.start_date ? new Date(form.start_date + 'T00:00:00Z').toISOString() : null,
        end_date: form.end_date ? new Date(form.end_date + 'T23:59:59Z').toISOString() : '',
      };
      if (isEdit) {
        await adminAPI.updateBusinessAd(ad.ad_id, payload);
        toast.success('Ad updated');
      } else {
        await adminAPI.createBusinessAd(payload);
        toast.success('Ad created');
      }
      onSave();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col overflow-hidden" onOpenAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-[#002FA7]" />
            {isEdit ? 'Edit Business Ad' : 'Create Business Ad'}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto space-y-4 pt-2 pb-2">
          {/* Business Info */}
          <div className="bg-zinc-50 rounded-lg p-4 space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Business Information</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2 sm:col-span-1">
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Business Name *</label>
                <input value={form.business_name} onChange={set('business_name')} placeholder="Acme Corp"
                  className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-base focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none" data-testid="ad-business-name" />
              </div>
              <div className="col-span-2 sm:col-span-1">
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Category</label>
                <select value={form.category} onChange={set('category')}
                  className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-base bg-white focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none" data-testid="ad-category">
                  <option value="">Select category...</option>
                  {AD_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Address</label>
                <input value={form.address} onChange={set('address')} placeholder="123 Main St"
                  className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-base focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none" data-testid="ad-address" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">City</label>
                <input value={form.city} onChange={set('city')} placeholder="Oshawa"
                  className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-base focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none" data-testid="ad-city" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Province</label>
                  <input value={form.province} onChange={set('province')} placeholder="Ontario"
                    className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-base focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none" data-testid="ad-province" />
                </div>
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Postal Code</label>
                  <input value={form.postal_code} onChange={set('postal_code')} placeholder="L1H 1A1"
                    className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-base focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none" data-testid="ad-postal-code" />
                </div>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Phone</label>
                <input value={form.phone} onChange={set('phone')} placeholder="(905) 555-1234"
                  className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-base focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none" data-testid="ad-phone" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Website</label>
                <input value={form.website} onChange={set('website')} placeholder="https://example.com"
                  className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-base focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none" data-testid="ad-website" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Email</label>
                <input value={form.email} onChange={set('email')} placeholder="info@example.com"
                  className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-base focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none" data-testid="ad-email" />
              </div>
            </div>
          </div>

          {/* Ad Content */}
          <div className="bg-zinc-50 rounded-lg p-4 space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Advertisement Content</p>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Ad Title *</label>
              <input value={form.ad_title} onChange={set('ad_title')} placeholder="Grand Opening Sale!"
                className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-base focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none" data-testid="ad-title" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Description</label>
              <textarea value={form.ad_description} onChange={set('ad_description')} rows={3} placeholder="Tell customers about your offer..."
                className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-base focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none resize-none" data-testid="ad-description" />
            </div>
            <div className="flex gap-6">
              <ImageUploader label="Business Logo" value={form.logo_image} onChange={(v) => setForm(p => ({...p, logo_image: v}))} testId="logo-upload-btn" />
              <div className="flex-1">
                <GalleryUploader images={form.image_gallery} onChange={(v) => setForm(p => ({...p, image_gallery: v}))} />
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="bg-zinc-50 rounded-lg p-4 space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Call to Action</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Button Text</label>
                <select value={form.cta_text} onChange={set('cta_text')}
                  className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-base bg-white focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none" data-testid="ad-cta-text">
                  {CTA_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">CTA URL</label>
                <input value={form.cta_url} onChange={set('cta_url')} placeholder="https://example.com/offer"
                  className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-base focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none" data-testid="ad-cta-url" />
              </div>
            </div>
          </div>

          {/* Schedule & Priority */}
          <div className="bg-zinc-50 rounded-lg p-4 space-y-3">
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Schedule & Priority</p>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Start Date</label>
                <input type="date" value={form.start_date} onChange={set('start_date')}
                  className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-base focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none" data-testid="ad-start-date" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">End Date</label>
                <input type="date" value={form.end_date} onChange={set('end_date')}
                  className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-base focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none" data-testid="ad-end-date" />
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-wider text-zinc-500 mb-1 block">Priority (1-10)</label>
                <input type="number" min="1" max="10" value={form.priority} onChange={set('priority')}
                  className="w-full border border-zinc-200 rounded-lg px-3 py-2 text-base focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none" data-testid="ad-priority" />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button type="button" onClick={() => setForm(p => ({...p, is_active: !p.is_active}))} className="flex items-center gap-2" data-testid="ad-active-toggle">
                {form.is_active ? <ToggleRight className="w-6 h-6 text-emerald-500" /> : <ToggleLeft className="w-6 h-6 text-zinc-400" />}
                <span className={`text-sm font-medium ${form.is_active ? 'text-emerald-700' : 'text-zinc-500'}`}>
                  {form.is_active ? 'Active' : 'Inactive'}
                </span>
              </button>
            </div>
          </div>
        </div>

        {/* Pinned bottom actions — outside scroll area */}
        <div className="flex justify-end gap-2 pt-3 border-t border-zinc-100 flex-shrink-0" data-testid="ad-form-actions">
          <button onClick={onClose} className="px-4 py-2.5 rounded-lg border border-zinc-200 text-base font-medium text-zinc-600 hover:bg-zinc-50" data-testid="ad-form-cancel">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={submitting}
            className="px-5 py-2.5 rounded-lg bg-[#002FA7] text-white text-base font-bold hover:bg-[#002585] disabled:opacity-50 flex items-center gap-2" data-testid="ad-form-submit">
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {isEdit ? 'Update Ad' : 'Create Ad'}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DeleteDialog({ ad, onClose, onConfirm }) {
  const [loading, setLoading] = useState(false);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="delete-ad-overlay">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center"><AlertTriangle className="w-5 h-5 text-red-500" /></div>
          <div>
            <h3 className="font-bold text-zinc-900">Delete Ad</h3>
            <p className="text-sm text-zinc-500">Permanently delete <strong>{ad.ad_title}</strong>?</p>
          </div>
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-zinc-200 text-sm font-medium text-zinc-600 hover:bg-zinc-50" data-testid="delete-ad-cancel">Cancel</button>
          <button onClick={async () => { setLoading(true); await onConfirm(); setLoading(false); }} disabled={loading}
            className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700 disabled:opacity-50 flex items-center gap-2" data-testid="delete-ad-confirm">
            {loading && <Loader2 className="w-3 h-3 animate-spin" />} Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminBusinessAds() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [formAd, setFormAd] = useState(undefined); // undefined=closed, null=create, object=edit
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [sendingReport, setSendingReport] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    try {
      const { data } = await adminAPI.getBusinessAdAnalytics();
      setAnalytics(data);
    } catch { /* silent */ }
  }, []);

  const fetchAds = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (filter) params.status = filter;
      const { data } = await adminAPI.getBusinessAds(params);
      setAds(data.ads || []);
      setTotalPages(data.pages || 1);
    } catch { toast.error('Failed to load ads'); }
    finally { setLoading(false); }
  }, [page, filter]);

  useEffect(() => { fetchAds(); }, [fetchAds]);
  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const handleSendReport = async () => {
    setSendingReport(true);
    try {
      const { data } = await adminAPI.sendBusinessAdReport();
      toast.success(data.message || 'Report sent!');
    } catch { toast.error('Failed to send report'); }
    finally { setSendingReport(false); }
  };

  const handleToggle = async (ad) => {
    try {
      const { data } = await adminAPI.toggleBusinessAd(ad.ad_id);
      toast.success(data.message);
      fetchAds();
    } catch { toast.error('Toggle failed'); }
  };

  const handleDelete = async () => {
    try {
      await adminAPI.deleteBusinessAd(deleteTarget.ad_id);
      toast.success('Ad deleted');
      setDeleteTarget(null);
      fetchAds();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div data-testid="admin-business-ads">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-zinc-900">Business Ads</h1>
          <p className="text-sm text-zinc-500 mt-1">Manage sponsored business advertisements</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleSendReport} disabled={sendingReport}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white border border-zinc-200 text-sm font-bold text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 transition-colors" data-testid="send-report-btn">
            {sendingReport ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            Send Report
          </button>
          <button onClick={() => setFormAd(null)}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#002FA7] text-white text-sm font-bold hover:bg-[#002585] transition-colors" data-testid="create-business-ad-btn">
            <Plus className="w-4 h-4" /> Create Business Ad
          </button>
        </div>
      </div>

      {/* Analytics Cards */}
      {analytics && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="analytics-cards">
          <div className="bg-white rounded-lg border border-zinc-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-sky-50 flex items-center justify-center"><Eye className="w-4 h-4 text-sky-600" /></div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Impressions</span>
            </div>
            <p className="text-2xl font-bold text-zinc-900" data-testid="total-impressions">{(analytics.total_impressions || 0).toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-lg border border-zinc-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center"><MousePointerClick className="w-4 h-4 text-violet-600" /></div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Clicks</span>
            </div>
            <p className="text-2xl font-bold text-zinc-900" data-testid="total-clicks">{(analytics.total_clicks || 0).toLocaleString()}</p>
          </div>
          <div className="bg-white rounded-lg border border-zinc-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-[#002FA7]/5 flex items-center justify-center"><TrendingUp className="w-4 h-4 text-[#002FA7]" /></div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Avg CTR</span>
            </div>
            <p className="text-2xl font-bold text-[#002FA7]" data-testid="avg-ctr">{analytics.avg_ctr || 0}%</p>
          </div>
          <div className="bg-white rounded-lg border border-zinc-200 p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center"><Activity className="w-4 h-4 text-emerald-600" /></div>
              <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400">Active Ads</span>
            </div>
            <p className="text-2xl font-bold text-emerald-600" data-testid="active-count">{analytics.active_count || 0}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-2 mb-6" data-testid="ad-filters">
        {[
          { value: '', label: 'All' },
          { value: 'active', label: 'Active' },
          { value: 'paused', label: 'Paused' },
          { value: 'scheduled', label: 'Scheduled' },
          { value: 'expired', label: 'Expired' },
        ].map(f => (
          <button key={f.value} onClick={() => { setFilter(f.value); setPage(1); }}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${filter === f.value ? 'bg-[#002FA7] text-white' : 'bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-50'}`}
            data-testid={`ad-filter-${f.value || 'all'}`}
          >{f.label}</button>
        ))}
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-zinc-200 overflow-hidden" data-testid="business-ads-table">
        {loading ? (
          <div className="p-10 text-center"><Loader2 className="w-6 h-6 text-[#002FA7] animate-spin mx-auto" /></div>
        ) : ads.length === 0 ? (
          <div className="p-10 text-center">
            <Megaphone className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
            <p className="text-sm text-zinc-400">No business ads found</p>
            <button onClick={() => setFormAd(null)} className="mt-3 text-sm font-bold text-[#002FA7] hover:underline">Create your first ad</button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-100 bg-zinc-50/50">
                  <th className="text-left px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Ad</th>
                  <th className="text-left px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Business</th>
                  <th className="text-left px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Period</th>
                  <th className="text-center px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Impressions</th>
                  <th className="text-center px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Clicks</th>
                  <th className="text-center px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">CTR</th>
                  <th className="text-center px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Priority</th>
                  <th className="text-center px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Status</th>
                  <th className="text-right px-5 py-3 font-semibold text-zinc-500 text-xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {ads.map(ad => {
                  const status = getAdStatus(ad);
                  return (
                    <tr key={ad.ad_id} className="border-b border-zinc-50 hover:bg-zinc-50/50 transition-colors" data-testid={`ad-row-${ad.ad_id}`}>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          {ad.logo_image ? (
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${ad.logo_image}`} alt="" className="w-9 h-9 rounded-lg object-cover border border-zinc-100" />
                          ) : (
                            <div className="w-9 h-9 rounded-lg bg-zinc-100 flex items-center justify-center"><ImageIcon className="w-4 h-4 text-zinc-400" /></div>
                          )}
                          <div>
                            <p className="font-semibold text-zinc-900 line-clamp-1 max-w-[180px]">{ad.ad_title}</p>
                            <p className="text-[10px] text-zinc-400">{ad.category || '—'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <p className="text-zinc-700 font-medium">{ad.business_name}</p>
                        <p className="text-[10px] text-zinc-400">{ad.city}{ad.city && ad.province ? ', ' : ''}{ad.province}</p>
                      </td>
                      <td className="px-5 py-4 text-xs text-zinc-500">
                        {formatDate(ad.start_date)} — {ad.end_date ? formatDate(ad.end_date) : 'Ongoing'}
                      </td>
                      <td className="px-5 py-4 text-center">
                        <span className="inline-flex items-center gap-1 text-sm font-mono"><Eye className="w-3 h-3 text-zinc-400" /> {(ad.impressions || 0).toLocaleString()}</span>
                      </td>
                      <td className="px-5 py-4 text-center">
                        <span className="inline-flex items-center gap-1 text-sm font-mono"><MousePointerClick className="w-3 h-3 text-zinc-400" /> {(ad.clicks || 0).toLocaleString()}</span>
                      </td>
                      <td className="px-5 py-4 text-center font-mono text-sm">{ctr(ad.impressions, ad.clicks)}</td>
                      <td className="px-5 py-4 text-center">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-zinc-100 text-zinc-600">{ad.priority || 1}</span>
                      </td>
                      <td className="px-5 py-4 text-center">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${status.color}`}>{status.label}</span>
                      </td>
                      <td className="px-5 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => setFormAd(ad)} className="p-1.5 rounded-lg text-zinc-400 hover:text-[#002FA7] hover:bg-zinc-100 transition-colors" title="Edit" data-testid={`edit-ad-${ad.ad_id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </button>
                          <button onClick={() => handleToggle(ad)} className="p-1.5 rounded-lg text-zinc-400 hover:text-emerald-600 hover:bg-zinc-100 transition-colors" title={ad.is_active ? 'Pause' : 'Activate'} data-testid={`toggle-ad-${ad.ad_id}`}>
                            {ad.is_active ? <ToggleRight className="w-3.5 h-3.5" /> : <ToggleLeft className="w-3.5 h-3.5" />}
                          </button>
                          {ad.cta_url && (
                            <a href={ad.cta_url} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-lg text-zinc-400 hover:text-sky-600 hover:bg-zinc-100 transition-colors" title="Visit CTA">
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                          <button onClick={() => setDeleteTarget(ad)} className="p-1.5 rounded-lg text-zinc-400 hover:text-red-600 hover:bg-zinc-100 transition-colors" title="Delete" data-testid={`delete-ad-${ad.ad_id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t border-zinc-100">
            <p className="text-xs text-zinc-500">Page {page} of {totalPages}</p>
            <div className="flex gap-1">
              <button onClick={() => setPage(Math.max(1, page - 1))} disabled={page <= 1}
                className="p-1.5 rounded-lg border border-zinc-200 text-zinc-400 hover:text-zinc-600 disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => setPage(Math.min(totalPages, page + 1))} disabled={page >= totalPages}
                className="p-1.5 rounded-lg border border-zinc-200 text-zinc-400 hover:text-zinc-600 disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>

      {/* Dialogs */}
      {formAd !== undefined && <BusinessAdForm ad={formAd} onSave={() => { setFormAd(undefined); fetchAds(); }} onClose={() => setFormAd(undefined)} />}
      {deleteTarget && <DeleteDialog ad={deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={handleDelete} />}
    </div>
  );
}
